const SILENT_WAV_BYTES = new Uint8Array([
  0x52, 0x49, 0x46, 0x46, 0x2c, 0x00, 0x00, 0x00,
  0x57, 0x41, 0x56, 0x45, 0x66, 0x6d, 0x74, 0x20,
  0x10, 0x00, 0x00, 0x00, 0x01, 0x00, 0x01, 0x00,
  0x40, 0x1f, 0x00, 0x00, 0x80, 0x3e, 0x00, 0x00,
  0x02, 0x00, 0x10, 0x00, 0x64, 0x61, 0x74, 0x61,
  0x08, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00,
]);

let mediaUnlocked = false;
let unlockPromise: Promise<void> | null = null;
let silentAudioUrl: string | null = null;
const MEDIA_UNLOCK_TIMEOUT_MS = 250;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function getSilentAudioUrl(): string {
  if (!silentAudioUrl) {
    silentAudioUrl = URL.createObjectURL(
      new Blob([SILENT_WAV_BYTES], { type: "audio/wav" })
    );
  }
  return silentAudioUrl;
}

export function isPlaybackPermissionError(error: unknown): boolean {
  const name =
    error && typeof error === "object" && "name" in error
      ? String((error as { name?: unknown }).name || "")
      : "";
  const message = error instanceof Error ? error.message : String(error || "");
  return (
    name === "NotAllowedError" ||
    /not allowed by the user agent|denied permission|notallowederror/i.test(
      message
    )
  );
}

export function describePlaybackError(
  error: unknown,
  fallback = "Playback failed"
): string {
  if (isPlaybackPermissionError(error)) {
    return "Playback was blocked by the browser. Interact with the page and try again.";
  }
  return error instanceof Error && error.message ? error.message : fallback;
}

export function configurePlaybackAudio(
  audio: HTMLAudioElement,
  volume?: number
): HTMLAudioElement {
  audio.preload = "auto";
  audio.setAttribute("playsinline", "");
  audio.setAttribute("webkit-playsinline", "");
  try {
    (audio as HTMLAudioElement & { playsInline?: boolean }).playsInline = true;
  } catch {
    // Ignore browsers that expose playsInline as a read-only property.
  }
  if (typeof volume === "number") {
    audio.volume = volume;
  }
  return audio;
}

export async function primeMediaPlayback(): Promise<void> {
  if (mediaUnlocked || typeof window === "undefined" || typeof Audio === "undefined") {
    return;
  }
  if (unlockPromise) return unlockPromise;

  unlockPromise = (async () => {
    const audio = configurePlaybackAudio(new Audio());
    audio.muted = true;
    audio.volume = 0;
    audio.src = getSilentAudioUrl();

    try {
      const playResult = audio.play();
      if (playResult && typeof playResult.then === "function") {
        await Promise.race([
          playResult
            .then(() => {
              mediaUnlocked = true;
            })
            .catch(() => {
              // Ignore unlock failures; the real playback path will surface them.
            }),
          sleep(MEDIA_UNLOCK_TIMEOUT_MS),
        ]);
      } else {
        mediaUnlocked = true;
      }
      audio.pause();
      try {
        audio.currentTime = 0;
      } catch {
        // Ignore reset failures on locked media elements.
      }
    } finally {
      audio.removeAttribute("src");
      try {
        audio.load();
      } catch {
        // Ignore cleanup failures.
      }
      unlockPromise = null;
    }
  })();

  return unlockPromise;
}
